import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Tag, Space, Popconfirm, App, Empty, Typography } from 'antd';
import { CheckOutlined, CloseOutlined, ReloadOutlined } from '@ant-design/icons';
import PujaSpinner from '../../components/PujaSpinner';
import { buyerService } from '../../services/api';
import dayjs from 'dayjs';

const { Text } = Typography;

/**
 * Solicitudes de acceso de compradores a ESTA cabaña.
 *
 * Existía todo el backend (`GET /admin/autorizaciones-pendientes`,
 * `PUT /admin/autorizaciones/:id`) y hasta las funciones en `api.js`, pero
 * ningún componente las llamaba: las solicitudes se creaban y no había dónde
 * verlas. Con el candado de oferta puesto, eso dejaba al comprador trabado sin
 * que la cabaña pudiera destrabarlo.
 *
 * Un comprador NO pertenece a la cabaña (`usuarios.id_clientes` sigue NULL):
 * esto habilita la relación comercial, no lo convierte en staff.
 */
const SolicitudesAcceso = ({ onCambio }) => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [solicitudes, setSolicitudes] = useState([]);
  const [resolviendo, setResolviendo] = useState({});

  const fetchSolicitudes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await buyerService.getAutorizacionesPend();
      setSolicitudes(res.data || []);
      onCambio?.((res.data || []).length);
    } catch (err) {
      message.error(err.response?.data?.error || 'No pudimos cargar las solicitudes');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchSolicitudes(); }, [fetchSolicitudes]);

  const responder = async (record, estado) => {
    setResolviendo(prev => ({ ...prev, [record.id_autorizacion]: true }));
    try {
      await buyerService.responderAutorizacion(record.id_autorizacion, estado);
      message.success(estado === 'autorizado'
        ? `${record.usuario?.nom_usuario || 'El comprador'} ya puede ofertar en tus remates`
        : 'Solicitud rechazada');
      await fetchSolicitudes();
    } catch (err) {
      message.error(err.response?.data?.error || 'No pudimos responder la solicitud');
    } finally {
      setResolviendo(prev => ({ ...prev, [record.id_autorizacion]: false }));
    }
  };

  const columnas = [
    {
      title: 'Comprador',
      key: 'comprador',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.usuario?.nom_usuario || `Usuario #${r.id_usuario}`}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.usuario?.email_usuario}</Text>
        </div>
      ),
    },
    {
      title: 'Solicitó',
      dataIndex: 'fecha_solicitud',
      key: 'fecha_solicitud',
      width: 160,
      render: (v) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—'),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      width: 110,
      render: () => <Tag color="orange">Pendiente</Tag>,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 210,
      render: (_, r) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            loading={!!resolviendo[r.id_autorizacion]}
            onClick={() => responder(r, 'autorizado')}
          >
            Autorizar
          </Button>
          {/* Rechazar no borra la solicitud: la deja en 'rechazado'. Queda el
              registro de que la cabaña decidió, y el comprador ve el estado. */}
          <Popconfirm
            title="¿Rechazar la solicitud?"
            description="No va a poder ofertar en tus remates."
            okText="Rechazar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            onConfirm={() => responder(r, 'rechazado')}
          >
            <Button danger size="small" icon={<CloseOutlined />}>
              Rechazar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) return <PujaSpinner />;

  return (
    <div>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Text type="secondary">
          Compradores que pidieron acceso para pre-ofertar en tus remates. Hasta que
          los autorices no pueden ofertar.
        </Text>
        <Button icon={<ReloadOutlined />} size="small" onClick={fetchSolicitudes}>
          Actualizar
        </Button>
      </Space>

      {solicitudes.length === 0 ? (
        <Empty description="No hay solicitudes pendientes" />
      ) : (
        <Table
          scroll={{ x: 'max-content' }}
          rowKey="id_autorizacion"
          dataSource={solicitudes}
          columns={columnas}
          pagination={{ pageSize: 20 }}
        />
      )}
    </div>
  );
};

export default SolicitudesAcceso;
